import { useContext, useEffect, useRef } from 'react'

import { type To, UNSAFE_NavigationContext } from 'react-router-dom'
import { useCommonStore } from 'shared/stores/commonStore'

// This product policy is intentionally separate from useHistory, which is
// only a compatibility polyfill for legacy microfrontends.
const openInNewTab = (url: string) =>
  window.open(url, '_blank', 'noopener,noreferrer')

const resolveHref = (to: To) => {
  if (typeof to === 'string') {
    const url = new URL(to, window.location.href)
    return `${url.pathname}${url.search}${url.hash}`
  }

  const location = to as {
    pathname?: string
    search?: string
    hash?: string
  }
  return `${location.pathname ?? window.location.pathname}${location.search ?? ''}${location.hash ?? ''}`
}

export const useMeetingNavigationGuard = () => {
  const { navigator } = useContext(UNSAFE_NavigationContext)
  const savedHistoryRef = useRef<{
    state: unknown
    href: string
  } | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined') return

    savedHistoryRef.current = {
      state: window.history.state,
      href: window.location.href
    }

    const originalPush = navigator.push
    const originalReplace = navigator.replace

    const updateSaved = () => {
      savedHistoryRef.current = {
        state: window.history.state,
        href: window.location.href
      }
    }

    const push = (to: To, state?: unknown) => {
      if (useCommonStore.getState().isClusterMeetOnline) {
        openInNewTab(resolveHref(to))
        return
      }
      originalPush.call(navigator, to, state)
      updateSaved()
    }

    const replace = (to: To, state?: unknown) => {
      if (useCommonStore.getState().isClusterMeetOnline) {
        openInNewTab(resolveHref(to))
        return
      }
      originalReplace.call(navigator, to, state)
      updateSaved()
    }

    const onPopState = (event: PopStateEvent) => {
      if (!useCommonStore.getState().isClusterMeetOnline) {
        updateSaved()
        return
      }

      event.stopImmediatePropagation()
      openInNewTab(window.location.href)
      const savedHistory = savedHistoryRef.current
      if (!savedHistory) return
      window.history.pushState(savedHistory.state, '', savedHistory.href)
    }

    navigator.push = push
    navigator.replace = replace
    window.addEventListener('popstate', onPopState, true)

    return () => {
      if (navigator.push === push) navigator.push = originalPush
      if (navigator.replace === replace) navigator.replace = originalReplace
      window.removeEventListener('popstate', onPopState, true)
    }
  }, [navigator, savedHistoryRef])
}
